import React, { useState } from "react";
import { formatDateWithTime } from "../../../utils/index.ts";
import { toCamelCase } from "../../../hooks/toCameCase.tsx";

const shiftColors: { [k: string]: string } = {
  Matutino: "#F7B917",
  Intermedio: "#1E90FF",
  Vespertino: "#DC2C27",
};

const parseBaseTime = (startTime: string | undefined): string | null => {
  if (!startTime) return null;

  return startTime.split(" - ")[0].trim();
};

const generateHeaderSlots = (
  baseTime: string | null,
  count: number,
): string[] => {
  if (!baseTime) return [];
  const [h, m] = baseTime.split(":").map(Number);
  const base = h * 60 + m;
  return Array.from({ length: count }, (_, i) => {
    const total = base + i * 30;
    const hh = Math.floor(total / 60) % 24;
    const mm = total % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  });
};

const ScheduleMiniPreview = ({ records }: { records: any[] }) => {
  if (!records || records.length === 0) return null;

  const allX = records.map((r) => Number(r.positionX));
  const allXW = records.map((r) => Number(r.positionX) + Number(r.positionW));
  const minX = Math.min(...allX);
  const maxXW = Math.max(...allXW);
  const timespan = maxXW - minX || 1;

  const minRecord = records.reduce(
    (min, r) => (Number(r.positionX) < Number(min.positionX) ? r : min),
    records[0],
  );
  const baseTime = parseBaseTime(minRecord.startTime);
  const headerSlots = generateHeaderSlots(baseTime, timespan);

  const sorted = [...records].sort(
    (a, b) => Number(a.positionY) - Number(b.positionY),
  );

  return (
    <div
      style={{
        padding: "10px 12px",
        borderTop: "1px solid #e5e7eb",
        backgroundColor: "#F9FAFB",
      }}
    >
      <p
        style={{
          fontSize: "11px",
          color: "#6B7280",
          fontWeight: 600,
          marginBottom: "8px",
          textTransform: "uppercase",
          letterSpacing: "0.4px",
        }}
      >
        Vista previa del horario
      </p>

      {/* Time header */}
      {headerSlots.length > 0 && (
        <div style={{ display: "flex", marginBottom: "6px" }}>
          <div style={{ width: "120px", flexShrink: 0 }} />
          <div
            style={{
              flex: 1,
              display: "flex",
              borderBottom: "1px solid #D1D5DB",
            }}
          >
            {headerSlots.map((slot, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: "8px",
                  color: "#6B7280",
                  borderLeft: i > 0 ? "1px solid #E5E7EB" : "none",
                  paddingBottom: "3px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",

                  visibility: i % 2 === 0 ? "visible" : "hidden",
                }}
              >
                {slot}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
        {sorted.map((record, i) => {
          const x = Number(record.positionX);
          const w = Number(record.positionW);
          const leftPct = ((x - minX) / timespan) * 100;
          const widthPct = Math.max((w / timespan) * 100, 4);
          const color = shiftColors[record.shift] || "#6B7280";
          const name = toCamelCase(
            `${record.partner?.person?.firstName ?? ""} ${record.partner?.person?.lastName ?? ""}`.trim(),
          );

          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "3px",
                height: "20px",
              }}
            >
              <div
                title={record.positionName}
                style={{
                  width: "120px",
                  flexShrink: 0,
                  fontSize: "10px",
                  color: "#374151",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingRight: "6px",
                }}
              >
                {record.positionName}
              </div>
              <div
                style={{
                  flex: 1,
                  position: "relative",
                  height: "16px",
                  backgroundColor: "#E5E7EB",
                  borderRadius: "3px",
                }}
              >
                {/* Column grid lines */}
                {Array.from({ length: timespan }).map((_, ci) => (
                  <div
                    key={ci}
                    style={{
                      position: "absolute",
                      left: `${(ci / timespan) * 100}%`,
                      top: 0,
                      bottom: 0,
                      borderLeft: ci > 0 ? "1px solid #D1D5DB" : "none",
                    }}
                  />
                ))}
                <div
                  title={name}
                  style={{
                    position: "absolute",
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    height: "100%",
                    backgroundColor: color,
                    borderRadius: "3px",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "4px",
                    overflow: "hidden",
                    zIndex: 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#fff",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ModalHistorySchedule = ({
  historyData,
  onView,
  onClose,
}: {
  historyData: any;
  onView: (records: any[]) => void;
  onClose: () => void;
}) => {
  const [showScrollIcon, setShowScrollIcon] = useState(true);
  const [expandedPreviews, setExpandedPreviews] = useState<Set<any>>(new Set());
  const normalizedHistoryData = Array.isArray(historyData) ? historyData : [];

  const handleScrollDown = () => {
    const container = document.getElementById("history-container");
    if (container) {
      container.scrollBy({ top: 500, behavior: "smooth" });
    }
    setShowScrollIcon(false);
  };

  const handleView = (records: any[]) => {
    onView(records);
    onClose();
  };

  const togglePreview = (id: any) => {
    setExpandedPreviews((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="container-fluid">
      <div
        id="history-container"
        style={{ maxHeight: "480px", overflowY: "auto", position: "relative" }}
      >
        {normalizedHistoryData.map((historyGroup: any) => (
          <div key={historyGroup.id} className="mb-3">
            <div className="card">
              <div className="card-header">
                <p>
                  <span className="fw-bold">Creado por:</span>{" "}
                  {historyGroup?.createdBy?.name}{" "}
                  {historyGroup?.createdBy?.lastName}
                </p>
                <p>
                  <span className="fw-bold mb-0"> Guardado el: </span>{" "}
                  {formatDateWithTime(historyGroup.createdAt)}
                </p>
                <div className="d-flex mt-0 align-items-center justify-content-between">
                  <p className="mb-0 fw-bold">
                    Numero de registros: {Array.isArray(historyGroup.records) ? historyGroup.records.length : 0}
                  </p>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => togglePreview(historyGroup.id)}
                      title={
                        expandedPreviews.has(historyGroup.id)
                          ? "Ocultar vista previa"
                          : "Ver vista previa"
                      }
                    >
                      <i className="fa-solid fa-chart-gantt"></i>{" "}
                      {expandedPreviews.has(historyGroup.id)
                        ? "Ocultar"
                        : "Ver"}
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() =>
                        handleView(
                          Array.isArray(historyGroup.records)
                            ? historyGroup.records
                            : [],
                        )
                      }
                    >
                      <i className="fa-solid fa-eye me-1"></i>
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
              {expandedPreviews.has(historyGroup.id) && (
                <ScheduleMiniPreview
                  records={
                    Array.isArray(historyGroup.records)
                      ? historyGroup.records
                      : []
                  }
                />
              )}
            </div>
          </div>
        ))}
        {normalizedHistoryData.length === 0 && (
          <div className="d-flex justify-content-center">
            <p>No hay historial de cambios</p>
          </div>
        )}

        {showScrollIcon && normalizedHistoryData.length > 3 && (
          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              cursor: "pointer",
            }}
            onClick={handleScrollDown}
          >
            <i className="fa-solid fa-arrow-down fa-2x"></i>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalHistorySchedule;
